import PropTypes from 'prop-types';
import { useEffect, useMemo, useState, useContext } from 'react';
import { useLocation } from 'react-router-dom';
// hooks
import useResponsive from '../../hooks/useResponsive';
import { useExpeditionCount } from '../../hooks/useExpeditionCount';
import { useFoundCount } from '../../hooks/useFoundCount';
import { useRestitutedCount } from '../../hooks/useRestitutedCount';
import { getConversationUnread } from 'src/service/conversationApi';
import AuthContext from 'src/contexts/AuthContext';
// components
import Scrollbar from '../../components/Scrollbar';
import NavSection from '../../components/NavSection';
import PingComponent from '../../utils/PingComponent';
import navConfig from './NavConfig';

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const { pathname } = useLocation();
  const isDesktop = useResponsive('up', 'lg');
  const { expeditionCount } = useExpeditionCount();
  const { foundCount } = useFoundCount();
  const { restitutedCount } = useRestitutedCount();
  const { customerId } = useContext(AuthContext);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);

  useEffect(() => {
    if (!customerId) return;
    let isMounted = true;
    const fetchUnread = async () => {
      const unread = await getConversationUnread(customerId);
      if (isMounted) setUnreadMessagesCount(unread.length);
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);

    // Ajout : écouteur pour mise à jour immédiate
    const handleMessageRead = () => fetchUnread();
    window.addEventListener('messageRead', handleMessageRead);

    return () => { 
      isMounted = false; 
      clearInterval(interval); 
      window.removeEventListener('messageRead', handleMessageRead);
    };
  }, [customerId]);

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Créer la configuration de navigation avec les notifications
  const navConfigWithNotification = useMemo(() => {
    return navConfig.map(item => {
      if (item.path === '/dashboard/products/aexpedie') {
        return {
          ...item,
          info: expeditionCount > 0 ? (
            <span style={{backgroundColor: '#FF4842', color: 'white'}} className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none rounded-full min-w-[20px] h-5">
              {expeditionCount > 9999 ? '9999+' : expeditionCount}
            </span>
          ) : null
        };
      }
      if (item.path === '/dashboard/products/trouves') {
        return {
          ...item,
          info: foundCount > 0 ? (
            <span style={{backgroundColor: '#54D62C', color: 'white'}} className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none rounded-full min-w-[20px] h-5">
              {foundCount > 9999 ? '9999+' : foundCount}
            </span>
          ) : null
        };
      }
      if (item.path === '/dashboard/products/restitues') {
        return {
          ...item,
          info: restitutedCount > 0 ? (
            <span style={{backgroundColor: '#3366FF', color: 'white'}} className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none rounded-full min-w-[20px] h-5">
              {restitutedCount > 9999 ? '9999+' : restitutedCount}
            </span>
          ) : null
        };
      }
      if (item.path === '/dashboard/messages') {
        return {
          ...item,
          info: unreadMessagesCount > 0 ? (
            <span style={{backgroundColor: '#FF4842', color: 'white'}} className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none rounded-full min-w-[20px] h-5">
              {unreadMessagesCount > 9999 ? '9999+' : unreadMessagesCount}
            </span>
          ) : null
        };
      }
      return item;
    });
  }, [expeditionCount, foundCount, restitutedCount, unreadMessagesCount]);

  const renderContent = (
    <Scrollbar className="h-full">
      <div className="px-4 pb-3 -mt-5">
        <div className="flex flex-col items-center space-y-3 pt-5 rounded-lg relative">
          <img src={`${process.env.REACT_APP_BASEURL}${process.env.REACT_APP_CUSTOMERLOGO}`} alt="Logo" className="w-full" />
        </div>
      </div>

      <NavSection navConfig={navConfigWithNotification} onLinkClick={!isDesktop ? onCloseSidebar : undefined} />

      {/* Indicateur de connexion API */}
      <div className="w-full flex justify-center my-4">
        <PingComponent />
      </div>

      <div className="flex-grow" />

      <div className="px-5 py-3 inline-flex">
        <div className="flex flex-col items-center w-full space-y-1">
          <div>
            <p className="text-xs">Powered by</p>
           
            <img src="/static/logo.svg" alt="Sherlook" className="w-[100px] mt-2" />
          </div>
        </div>
      </div>
    </Scrollbar>
  );

  return (
    <div className="lg:flex-shrink-0 lg:w-[280px]">
      {!isDesktop && (
        <div
          className={`fixed inset-y-0 left-0 z-50 w-[280px] transform transition-transform duration-300 ease-in-out ${
            isOpenSidebar ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="h-full bg-white shadow-lg">
            {renderContent}
          </div>
        </div>
      )}

      {isDesktop && (
        <div className="fixed inset-y-0 left-0 z-50 w-[280px] bg-white border-r border-dashed">
          {renderContent}
        </div>
      )}
    </div>
  );
}
